import Bugsnag from "@bugsnag/js";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiCalendar, FiChevronDown, FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { get, ref, set } from "firebase/database";
import { logout } from "../../Store/Actions";
import { FIREDB } from "../../firebaseInstance";
import { trackEvent } from "../../helperFunctions";
import useMobileView from "../../hooks/useMobileView";
import {
  getAutoReplyWithEndTime,
  statusOptions,
} from "../../metadata/chatInfoData";
import { CF_PROVIDER_ROLE } from "../../variable";
import Datepicker from "../ProviderProfile/Availability/DatePicker";
import { Button } from "../common/Buttons";
import FlexBox from "../common/FlexBox";
import { Body2, H2, H4, H6 } from "../common/Headings";
import Loader from "../common/Loader";
import { Modal } from "../common/Modal";
import Radio from "../common/Radio";
import {
  ACCENT_100,
  ACCENT_500,
  ACCENT_800,
  DAVYS_GREY_100,
  MOSS_GREEN_800,
} from "../common/colors";

const Container = styled(FlexBox)`
  height: 100%;
`;

const Header = styled(FlexBox)`
  border-bottom: 1px solid ${ACCENT_500};
  padding: 1rem 1.5rem;
  justify-content: space-between;
  align-items: center;
  @media screen and (max-width: 768px) {
    border-bottom: none;
    justify-content: flex-start;
    column-gap: 0.5rem;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 20;
  }
`;

const Content = styled(FlexBox)`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

const Footer = styled(FlexBox)`
  padding: 1.25rem;
  border-top: 1px solid ${ACCENT_500};
  justify-content: center;
  @media screen and (max-width: 768px) {
    position: fixed;
    border-top: none;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 20;
  }
`;

const CloseIcon = styled(FlexBox)`
  justify-content: center;
  align-items: center;
`;

const StatusOption = styled(FlexBox)`
  padding: 0.5rem 1rem;
  justify-content: center;
  align-items: center;
  border-radius: 0.5rem;
  border: 1px solid var(--davys-400);
  background: ${({ isSelected }) =>
    isSelected ? MOSS_GREEN_800 : DAVYS_GREY_100};
  color: ${({ isSelected }) => (isSelected ? ACCENT_100 : ACCENT_800)};
  font-weight: 700;
  font-size: 0.875rem;
  line-height: 1.5rem;
  cursor: pointer;
  transition: all 100ms ease-in-out;
`;

const FieldBox = styled(FlexBox)`
  position: relative;
  align-items: center;
  border: 1px solid #bdbdbd;
  border-radius: 0.5rem;
  background: white;
  height: 3rem;
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container input {
    border: none;
    outline: none;
    background: transparent;
    font-family: "Quicksand";
    font-size: 0.875rem;
    color: #4c4c4c;
    padding: 0 2.5rem 0 0.75rem;
    height: 100%;
  }
  select {
    appearance: none;
    border: none;
    outline: none;
    background: transparent;
    width: 100%;
    height: 100%;
    padding: 0 2.5rem 0 0.75rem;
    font-family: "Quicksand";
    font-size: 0.875rem;
    color: #4c4c4c;
    cursor: pointer;
  }
  .field-clear {
    position: absolute;
    right: 2.5rem;
    cursor: pointer;
  }
  .field-icon {
    position: absolute;
    right: 0.75rem;
    pointer-events: none;
  }
`;

const ONLINE_STATUS = "Online";
const OFFLINE_STATUS = "Offline";
const acceptableAvailabilityTypes = [ONLINE_STATUS, OFFLINE_STATUS];
const END_OF_DAY_MINUTES = 23 * 60 + 59;
const EXPIRING_STATUSES = ["Out Of Office", "On Leave"];

// 15-minute slots in minutes since midnight: 0, 15, ... 1425
const TIME_SLOTS = Array.from({ length: 96 }, (_, i) => i * 15);
const formatSlot = minutes =>
  dayjs().startOf("day").add(minutes, "minute").format("hh:mm A");

const getExpiresAt = (date, minutes) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    Math.floor(minutes / 60),
    minutes % 60
  ).getTime();

const StatusModal = ({ toggleModal }) => {
  const isMobile = useMobileView();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState(null);
  const [statusSelected, setStatusSelected] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endMinutes, setEndMinutes] = useState(null);
  const firebaseId = useSelector(state => state?.auth?.user?.firebaseid);
  const name = useSelector(state => state?.auth?.user?.name);
  const isCfProvider = useSelector(state =>
    state.provider.providerProfile?.roles?.some(role =>
      CF_PROVIDER_ROLE.includes(role)
    )
  );
  const userType = useSelector(state => state.auth.user?.usertype);
  const autoReplyProviderTypeKey = isCfProvider ? "childrenFirst" : "amaha";

  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    if (isMobile) {
      const update = () => {
        setHeaderHeight(headerRef.current?.offsetHeight || 0);
        setFooterHeight(footerRef.current?.offsetHeight || 0);
      };
      update();
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!firebaseId) return;
    getCurrentStatus();
  }, [firebaseId]);

  useEffect(() => {
    document.body.classList.add("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, []);

  const getCurrentStatus = () => {
    if (!firebaseId) return;
    let providerStatusDone = false,
      statusDone = false;

    (async () => {
      try {
        const snapshot = await get(ref(FIREDB, "providerStatus/" + firebaseId));
        let userStatus = snapshot.val();
        if (userStatus) {
          const status = userStatus?.status;
          const statusObj = statusOptions?.find(
            element => element?.status?.value === status
          );
          setStatusSelected(statusObj || null);
          const expiresAt = userStatus?.expiresAt;
          if (
            EXPIRING_STATUSES.includes(status) &&
            typeof expiresAt === "number" &&
            expiresAt > Date.now()
          ) {
            const expiry = new Date(expiresAt);
            setEndDate(expiry);
            // A date-only expiry is stored as 23:59, which isn't a slot, so
            // the time field stays empty for it.
            const minutes = expiry.getHours() * 60 + expiry.getMinutes();
            setEndMinutes(minutes % 15 === 0 ? minutes : null);
          }
        }
        providerStatusDone = true;
        if (statusDone && providerStatusDone) setLoading(false);
      } catch (err) {
        if (err?.code === "storage/unauthorized") {
          dispatch(logout());
        }
        console.log({ err });
        Bugsnag.notify(err);
      }
    })();

    (async () => {
      try {
        const snapshot = await get(ref(FIREDB, "Status/" + firebaseId));
        let providerStatus = snapshot.val();
        if (providerStatus) {
          if (acceptableAvailabilityTypes?.includes(providerStatus)) {
            setAvailability(providerStatus);
          }
        } else {
          // if no status saved then make online as default availability
          setAvailability(ONLINE_STATUS);
        }
        statusDone = true;
        if (statusDone && providerStatusDone) setLoading(false);
      } catch (err) {
        if (err?.code === "storage/unauthorized") {
          dispatch(logout());
        }
        Bugsnag.notify(err);
        setAvailability(ONLINE_STATUS);
      }
    })();
  };

  const statusValue = statusSelected?.status?.value;
  const needsEnd = EXPIRING_STATUSES.includes(statusValue);
  // Without a date the time means "today", so past slots are disabled then too
  const disablePastSlots = !endDate || dayjs(endDate).isSame(dayjs(), "day");
  const nowMinutes = dayjs().hour() * 60 + dayjs().minute();

  const hasEnd = needsEnd && (!!endDate || endMinutes !== null);
  // date only: end of that day; time only: today at that time
  const endLabel = hasEnd
    ? [
        endDate && dayjs(endDate).format("DD MMM YYYY"),
        endMinutes !== null && formatSlot(endMinutes),
      ]
        .filter(Boolean)
        .join(", ")
    : null;
  const baseAutoReply = statusSelected?.autoReply?.[autoReplyProviderTypeKey]
    ? statusSelected?.autoReply?.[autoReplyProviderTypeKey]?.value.replace(
        "<providerType>",
        userType
      )
    : null;
  const finalAutoReply = getAutoReplyWithEndTime(
    statusValue,
    autoReplyProviderTypeKey,
    baseAutoReply,
    endLabel
  );

  const handleSubmit = () => {
    if (!firebaseId) return;

    const expiresAt = hasEnd
      ? getExpiresAt(
          endDate || new Date(),
          endMinutes === null ? END_OF_DAY_MINUTES : endMinutes
        )
      : null;

    set(ref(FIREDB, "providerStatus/" + firebaseId), {
      autoreply: finalAutoReply,
      expiresAt,
      status: statusSelected?.status
        ? statusSelected?.status?.value === "Unavailable" &&
          autoReplyProviderTypeKey === "amaha"
          ? "Not Available At Amaha"
          : statusSelected?.status?.value
        : null,
    }).catch(err => {
      Bugsnag.notify(err);
    });

    if (acceptableAvailabilityTypes?.includes(availability)) {
      set(ref(FIREDB, "Status/" + firebaseId), availability).catch(err => {
        Bugsnag.notify(err);
      });
    }
    trackEvent({
      event: "status_confirm_click",
      payload: {
        provider_name: name,
        availability,
        status: statusSelected?.status?.value,
        "auto reply": finalAutoReply,
      },
    });
    toggleModal();
  };

  return (
    <Modal S borderRadius="1rem">
      <Container column>
        <Header ref={headerRef}>
          {isMobile && (
            <CloseIcon onClick={toggleModal}>
              <FiArrowLeft strokeWidth="3" size="1.25rem" color={ACCENT_800} />
            </CloseIcon>
          )}
          {isMobile ? (
            <H2 bold>Chat Availability </H2>
          ) : (
            <H4 bold>Chat Availability</H4>
          )}
          {!isMobile && (
            <CloseIcon>
              <FiX
                size="1.25rem"
                fontWeight={700}
                cursor="pointer"
                onClick={toggleModal}
              />
            </CloseIcon>
          )}
        </Header>
        {loading ? (
          <Loader />
        ) : (
          <Content
            column
            style={
              isMobile
                ? { marginTop: headerHeight, marginBottom: footerHeight }
                : {}
            }
          >
            <Body2 bold margin="0 0 0.5rem 0">
              Availability
            </Body2>
            <FlexBox columnGap="1.5rem">
              <FlexBox
                align="center"
                columnGap="0.25rem"
                onClick={() => setAvailability(ONLINE_STATUS)}
              >
                <Radio active={availability === ONLINE_STATUS} />
                <H4 bold>Online</H4>
              </FlexBox>
              <FlexBox
                align="center"
                columnGap="0.25rem"
                onClick={() => setAvailability(OFFLINE_STATUS)}
              >
                <Radio active={availability === OFFLINE_STATUS} />
                <H4 bold>Away</H4>
              </FlexBox>
            </FlexBox>

            <H6 bold margin="1.5rem 0 0.5rem 0">
              Chat Availability
            </H6>
            <FlexBox wrap="wrap" columnGap="1rem" rowGap="1rem">
              {statusOptions.map(option => (
                <StatusOption
                  key={option?.status?.value}
                  onClick={() => setStatusSelected(option)}
                  isSelected={
                    option?.status?.value === statusSelected?.status?.value
                  }
                >
                  {option?.status?.label === "Unavailable" &&
                  autoReplyProviderTypeKey === "amaha"
                    ? "Not Available At Amaha"
                    : option?.status?.label}
                </StatusOption>
              ))}
            </FlexBox>

            {needsEnd && (
              <FlexBox column margin="1.5rem 0 0 0">
                <FlexBox columnGap="1.5rem" wrap="wrap" rowGap="1rem">
                  <FlexBox
                    column
                    rowGap="0.5rem"
                    style={{ flex: 1, maxWidth: 400 }}
                  >
                    <Body2 bold>
                      {statusValue === "On Leave"
                        ? "On leave till date"
                        : "Out of office till date"}
                    </Body2>
                    <FieldBox>
                      <Datepicker
                        selectedDate={endDate}
                        handleChange={date => {
                          setEndDate(date);
                          if (
                            (!date || dayjs(date).isSame(dayjs(), "day")) &&
                            endMinutes !== null &&
                            endMinutes <= nowMinutes
                          ) {
                            setEndMinutes(null);
                          }
                        }}
                        allowToday
                        maxDate={dayjs().add(1, "year").toDate()}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="DD/MM/YYYY"
                      />
                      {endDate && (
                        <FiX
                          className="field-clear"
                          color="#4C4C4C"
                          onClick={() => {
                            setEndDate(null);
                            if (
                              endMinutes !== null &&
                              endMinutes <= nowMinutes
                            ) {
                              setEndMinutes(null);
                            }
                          }}
                        />
                      )}
                      <FiCalendar className="field-icon" color="#4C4C4C" />
                    </FieldBox>
                  </FlexBox>
                  <FlexBox
                    column
                    rowGap="0.5rem"
                    style={{ flex: 1, maxWidth: 400 }}
                  >
                    <Body2 bold>Time</Body2>
                    <FieldBox>
                      <select
                        value={endMinutes === null ? "" : endMinutes}
                        onChange={e => {
                          setEndMinutes(
                            e.target.value === ""
                              ? null
                              : Number(e.target.value)
                          );
                        }}
                      >
                        <option value="">Select time</option>
                        {TIME_SLOTS.map(minutes => (
                          <option
                            key={minutes}
                            value={minutes}
                            disabled={disablePastSlots && minutes <= nowMinutes}
                          >
                            {formatSlot(minutes)}
                          </option>
                        ))}
                      </select>
                      <FiChevronDown className="field-icon" color="#4C4C4C" />
                    </FieldBox>
                  </FlexBox>
                </FlexBox>
              </FlexBox>
            )}

            {statusSelected?.autoReply?.[autoReplyProviderTypeKey] && (
              <>
                <H6 bold margin="1.5rem 0 0.5rem 0">
                  Auto-reply message:
                </H6>
                <H6>{finalAutoReply}</H6>
              </>
            )}
          </Content>
        )}
        <Footer ref={footerRef}>
          <Button disabled={loading} onClick={handleSubmit} width="100%">
            SAVE CHANGES
          </Button>
        </Footer>
      </Container>
    </Modal>
  );
};

export default StatusModal;
